import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Button from '@shared/components/Buttons/Button.js';
import { Breakpoints } from '@shared/styles/index.js';

const BRAND_COLOR_PRIMARY = '#0b74da';
const BRAND_COLOR_SECONDARY = '#78bbf1';

export const BannerCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin: 12px var(--spacing-md);
  width: calc(100% - calc(var(--spacing-md) * 2));

  @media (max-width: ${Breakpoints.md}) {
    margin: 8px var(--spacing-md);
    width: calc(100% - calc(var(--spacing-md) * 2));
    padding: 12px;
    gap: 12px;
    border-radius: 10px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  width: 100%;

  @media (max-width: ${Breakpoints.md}) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const LogoSection = styled.div`
  flex-shrink: 0;
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
  min-width: 0;
`;

export const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${Breakpoints.md}) {
    gap: 12px;
  }
`;

export const ReactionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 12px 16px;
  background: rgba(248, 250, 255, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(11, 116, 218, 0.08);
  flex-shrink: 0;

  @media (max-width: ${Breakpoints.md}) {
    width: 100%;
    align-items: center;
    flex-direction: row;
    justify-content: center;
  }
`;

export const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 4px;

  @media (max-width: ${Breakpoints.md}) {
    gap: 6px;
  }
`;

export const SectionTab = styled.button`
  background: rgba(11, 116, 218, 0.06);
  border: 1px solid rgba(11, 116, 218, 0.15);
  color: ${BRAND_COLOR_PRIMARY};
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(11, 116, 218, 0.12);
    border-color: rgba(11, 116, 218, 0.25);
  }

  &:active {
    background: rgba(11, 116, 218, 0.18);
  }
`;

export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 4px;

  @media (max-width: ${Breakpoints.md}) {
    gap: 8px;
  }
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: #4a3f33;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:active {
    background: rgba(0, 0, 0, 0.04);
  }

  svg {
    color: ${BRAND_COLOR_PRIMARY};
  }
`;

export const StickyBar = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 24px;
  background: linear-gradient(135deg, rgba(11, 116, 218, 0.05) 0%, rgba(120, 187, 241, 0.08) 100%);
  backdrop-filter: blur(16px);
  position: fixed;
  top: 0;
  left: var(--sidebar-width, 0px);
  right: 0;
  width: calc(100% - var(--sidebar-width, 0px));
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(11, 116, 218, 0.12);
  border-bottom: 1px solid rgba(11, 116, 218, 0.1);

  @media (max-width: ${Breakpoints.md}) {
    padding: 8px 12px;
    gap: 12px;
  }
`;

export const StickyCompanyName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #2a2018;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export const StickyTabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-grow: 1;
  flex-wrap: wrap;
`;

export const StickyTab = styled.button`
  background: rgba(11, 116, 218, 0.08);
  border: 1px solid rgba(11, 116, 218, 0.15);
  color: ${BRAND_COLOR_PRIMARY};
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(11, 116, 218, 0.15);
    border-color: rgba(11, 116, 218, 0.25);
  }

  &:active {
    background: rgba(11, 116, 218, 0.2);
  }
`;

export const CompanyName = styled.h1`
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #0b74da 0%, #78bbf1 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  cursor: pointer;
  margin: 0;

  &:hover {
    opacity: 0.8;
  }
`;

export const BannerWrapper = styled(motion.div)<{ $isSticky?: boolean }>`
  background: linear-gradient(135deg, rgba(11, 116, 218, 0.03) 0%, rgba(120, 187, 241, 0.05) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(11, 116, 218, 0.12);
  padding: 24px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  box-shadow: 0 4px 20px rgba(11, 116, 218, 0.08);
  margin: 16px var(--spacing-md) 0;
  width: calc(100% - calc(var(--spacing-md) * 2));
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${BRAND_COLOR_PRIMARY} 0%,
      ${BRAND_COLOR_SECONDARY} 50%,
      ${BRAND_COLOR_PRIMARY} 100%
    );
    background-size: 200% 100%;
    opacity: 0.8;
    animation: gradientShift 3s ease infinite;
  }

  @keyframes gradientShift {
    0%, 100% { background-position: 0% 0%; }
    50% { background-position: 100% 0%; }
  }

  @media (max-width: ${Breakpoints.md}) {
    border-radius: 12px;
    padding: 16px;
    align-items: flex-start;
    flex-direction: column;
    margin: 12px var(--spacing-md) 8px;
    width: calc(100% - calc(var(--spacing-md) * 2));
  }
`;
export const MobileMetadataRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
`;

export const MobileDropdownContainer = styled(Box)`
  width: 100%;

  & .MuiSelect-root {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(8px);
    border-radius: 10px;
    border: 1px solid rgba(11, 116, 218, 0.2);
    transition: all 0.3s ease;
    font-size: 0.875rem;

    &:hover {
      border-color: ${BRAND_COLOR_PRIMARY};
      box-shadow: 0 4px 12px rgba(11, 116, 218, 0.15);
    }

    &.Mui-focused {
      border-color: ${BRAND_COLOR_PRIMARY};
      box-shadow: 0 4px 16px rgba(11, 116, 218, 0.2);
    }
  }

  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  & .MuiSelect-select {
    padding: 10px 14px;
    font-weight: 600;
    color: ${BRAND_COLOR_PRIMARY};
  }

  & .MuiMenuItem-root {
    transition: all 0.2s ease;
    font-size: 0.875rem;
    padding: 10px 16px;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(11, 116, 218, 0.1) 0%,
        rgba(120, 187, 241, 0.08) 100%
      );
      transform: translateX(4px);
    }
  }
`;

export const MobileActionsButton = styled(Button)`
  width: 100%;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-transform: none;
  font-size: 0.875rem;

  &:hover {
    background: linear-gradient(135deg, #45a049 0%, #4caf50 100%);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
`;

export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.1,
    },
  },
};

export const BackToTopButton = styled(motion.button)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${BRAND_COLOR_PRIMARY} 0%, ${BRAND_COLOR_SECONDARY} 100%);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(11, 116, 218, 0.3);
  z-index: 999;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(11, 116, 218, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: ${Breakpoints.md}) {
    width: 36px;
    height: 36px;
    bottom: 20px;
    right: 20px;
    font-size: 16px;
  }
`;
